'use client';

import {CSSProperties, ReactNode, useCallback, useEffect, useState} from 'react';
import {GAME_IFRAME_ID, sendMessageToGame, shapeMatchForGame} from '@/services/internal/sdk/sdk';
import LoadingBar from '@/app/games/[gid]/_src/LoadingBar';
import LoadingScreen from '@/app/games/[gid]/_src/LoadingScreen';
import {clearJoinSession} from '@/services/internal/tournament/join/session';
import submitScore from '@/services/internal/match/submitScore';
import {MatchState} from '@/services/internal/match/interfaces';
import abortMatch from '@/services/internal/match/abortMatch';
import {getUserId} from '@/services/internal/user/getUserId';
import {fingerprintStore} from '@/utils/fingerprints/FingerprintStore';
import {FTUE_STATE, GAME_UNITY_SDK_LS_NAME} from '@/utils/constant';
import Preferences from '@/sdk-mobile-web/Preferences/Preferences';
import {usePathname, useRouter} from 'next/navigation';
import HeartbeatManager from '../HeartbeatManager';
import {ReplaySettings} from '@/app/games/[gid]/_src/hooks/useReplaySettings';
import {useMemoryObserver} from './hooks/useMemoryObserver';
import {useReplayRecording} from './hooks/useReplayRecording';
import {
  getProgressionUserData,
  updateProgressionUserData,
} from '@/services/internal/progressions/userData';
import {
  getCurrentSeason,
  getNextSeasons,
  getPreviousSeasons,
} from '@/services/internal/progressions/seasons';
import {getFtueCookieClient, setFtueCookie} from '@/utils/ftueCookieHelper';
import {usePreventNavigation} from '@/app/games/[gid]/_src/GameProviders/hooks/usePreventNavigation';

export type MatchDetails = {
  match: any; // SKZMatch but can't import sdk-mobile here;
  tournament: any; // SKZTournament but can't import sdk-mobile here;;
};

// Note, displayResults is being passed an SKZMatch object.
// However, this component is not importing sdk-mobile, so it can't be typed here.
// This is also why we are forward declaring EmitterSubscription.
declare interface EmitterSubscription {
  remove: () => void;
}

type Props = {
  gid: string;
  replaySettings?: ReplaySettings;
  loadLight?: boolean;
  gameCdnUrl: string;
  matchToken?: string;
  children?: ReactNode;
  displayResults?: (any) => void; // any is SKZMatch
  preload?: boolean;
  addStartMatchListener?: (
    callback: (details: MatchDetails | null) => null | undefined
  ) => EmitterSubscription;
};

export default function GameProvider({
  gid,
  children,
  matchToken,
  displayResults,
  preload = false,
  addStartMatchListener,
  replaySettings,
  loadLight,
}: Props) {
  const [gameInitialized, setGameInitialized] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [matchDetails, setMatchDetails] = useState<MatchDetails | null>(null);
  const [showProgressionRoom, setShowProgressionRoom] = useState<boolean>(false);
  const pathname = usePathname();
  const router = useRouter();

  const stillLoading = loadingProgress < 1;
  const showLoadingBar = !gameInitialized && stillLoading && !matchDetails;
  const showLoadingScreen = !gameInitialized && matchDetails;
  const isMatchOngoing = gameInitialized && matchDetails;

  useMemoryObserver();

  const captureBackButtonCallback = useCallback(() => {
    if (showProgressionRoom) {
      setShowProgressionRoom(false);
    } else {
      // Reset match details and enable children to render
      setMatchDetails(null);
    }
    // Reset reinitialize game. Fixes sound playing on back/refresh and should release memory usage
    sendMessageToGame('ResetGame', {});
    HeartbeatManager.stopHeartbeatTimer();
  }, [showProgressionRoom]);

  usePreventNavigation({
    isLocked: !!isMatchOngoing || showProgressionRoom,
    isSilent: showProgressionRoom,
    captureBackButtonCallback,
  });

  const {startStreaming, stopStreaming} = useReplayRecording(replaySettings);

  // Handle loading progress to ensure the game is initialized before starting
  // TODO: This is a temporary solution to ensure the game is fully loaded before starting.
  useEffect(() => {
    if (loadingProgress >= 1) {
      const timer = setTimeout(() => {
        setGameInitialized(true);
      }, 3000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [loadingProgress]);

  // Initialize the match and send messages to the game
  useEffect(() => {
    if (!matchDetails || !gameInitialized) {
      console.log(
        'REPLAY: Game not initialized or match details are null, skipping initialization.'
      );
      return;
    }

    const {match, tournament} = matchDetails;
    const userId = getUserId();
    const random = {seed: match.random_number_seeds};
    const sdkMatch = shapeMatchForGame(match, tournament, userId);
    const player = sdkMatch.players.find((p) => p.id == Number(userId));

    const payload = {
      match: sdkMatch,
      matchToken: matchToken,
      random,
      player,
    };
    switch (getFtueCookieClient()) {
      case FTUE_STATE.ANONYMOUS:
        // First FTUE game is about to start
        setFtueCookie(FTUE_STATE.REGISTERED);
        Preferences.enableSecondFTUE();
        break;
      case FTUE_STATE.REGISTERED:
        // Second game is about to start
        setFtueCookie(FTUE_STATE.FINISHED);
        Preferences.disableFTUE();
        break;
      default:
        break;
    }

    const isCash = !!matchDetails?.tournament?.isCashTournament();
    HeartbeatManager.startHeartbeatTimer(isCash, matchDetails, gid);

    console.log(
      'REPLAY: Sending OnMatchWillBegin message to game.: ' + JSON.stringify(matchDetails)
    );

    sendMessageToGame('OnMatchWillBegin', payload);
    if (match.record_replay && player) {
      startStreaming(player.playerMatchId); // Start streaming when match starts
    }
  }, [gameInitialized, matchDetails, matchToken]);

  useEffect(() => {
    const isTutorialPath = pathname?.includes(`/games/${gid}/tutorial`);
    if (gameInitialized) {
      setShowTutorial(isTutorialPath);
      if (!showTutorial && isTutorialPath) {
        sendMessageToGame('OnTutorialScreenEnter', {});
      }
    }
  }, [gameInitialized, pathname, gid]);

  // Start the match and streaming process
  const startMatch = useCallback(
    (details: MatchDetails | null) => {
      if (!details) {
        return null;
      }
      setMatchDetails(details);
      setTimeout(clearJoinSession, 2000);
    },
    [setMatchDetails]
  );

  // End the match and stop streaming
  const endMatch = useCallback(() => {
    const match = matchDetails?.match;
    if (displayResults && match) {
      // delay the display of results to allow the router to mount
      setTimeout(() => {
        displayResults(match);
      }, 300);
    }
    setMatchDetails(null);
    HeartbeatManager.stopHeartbeatTimer();
    console.log('REPLAY: Match ended, stopping streaming.');
    stopStreaming(); // Stop streaming when match ends
  }, [matchDetails?.match, displayResults, setMatchDetails]);

  const isCash = !!matchDetails?.tournament?.isCashTournament();

  useEffect(() => {
    if (addStartMatchListener) {
      const startMatchSubscription = addStartMatchListener(startMatch);
      return () => {
        startMatchSubscription.remove();
      };
    } else {
      return () => {};
    }
  }, [startMatch, addStartMatchListener]);

  useEffect(() => {
    const handleProgressionRoomEnter = () => {
      // Make unity iframe visible
      setShowProgressionRoom(true);
      // Send event to unity to start progression room
      sendMessageToGame('OnProgressionRoomEnter', {});
    };

    window.addEventListener('OnProgressionRoomEnter', handleProgressionRoomEnter);

    return () => {
      window.removeEventListener('OnProgressionRoomEnter', handleProgressionRoomEnter);
    };
  }, []);

  // Below we are adding the listener to receive events from the iframe
  const src = process.env.NEXT_PUBLIC_TEST_UNITY_GAME || `/unity-games/${gid}/`;
  const origin = process.env.NEXT_PUBLIC_TEST_UNITY_GAME
    ? new URL(src).origin
    : window.location.origin;

  useEffect(() => {
    const readMessageFromGame = async (e: MessageEvent) => {
      if (e.origin !== origin) {
        return; // Only accept messages from the game
      }

      try {
        switch (e.data.topic) {
          case 'GameFrameStarted':
            fingerprintStore.setUnityConfig(e.data.config, gid);
            break;

          case 'UnityInitializeBegan':
            if (e.data.config.unitySdkGitVersion) {
              window.localStorage.setItem(GAME_UNITY_SDK_LS_NAME, e.data.config.unitySdkGitVersion);
            } else {
              window.localStorage.removeItem(GAME_UNITY_SDK_LS_NAME);
            }
            fingerprintStore.setUnityConfig(e.data.config, gid);
            break;

          case 'OnGameLoadProgress':
            if (!preload) {
              setLoadingProgress(Number(e.data.progress));
            }
            fingerprintStore.setUnityConfig(e.data.config, gid);
            break;

          case 'LaunchSkillz': {
            if (!preload) {
              if (showTutorial) {
                setShowTutorial(false);
                router.push(`/games/${gid}/play`);
              } else if (gameInitialized) {
                setMatchDetails(null);
              } else {
                setGameInitialized(true);
              }
            }
            break;
          }
          case 'SubmitScore':
            await submitScore({
              data: e.data.score,
              state: MatchState.FINISHED,
              isCash,
              gameId: gid,
              token: Buffer.from(JSON.stringify(matchDetails)).toString('base64'),
            });

            sendMessageToGame(`${e.data.topic}:callback`, {});
            break;
          case 'AbortMatch':
            abortMatch(e.data.data, isCash, gid);
            break;
          case 'UpdateScore':
            HeartbeatManager.setCurrentHeartbeatValue(e.data.score);
            break;
          case 'ReturnToSkillz': {
            if (showProgressionRoom) {
              setShowProgressionRoom(false);
              router.push(`/games/${gid}/play`);
            } else if (showTutorial) {
              setShowTutorial(false);
              router.push(`/games/${gid}/play`);
            } else {
              endMatch();
            }
            break;
          }
          case 'UnityInitialized':
            fingerprintStore.setUnityInstanceString(e.data.unityInstance);
            fingerprintStore.setUnityConfig(e.data.config, gid);
            break;

          case 'GetProgressionUserData': {
            const data: {
              progressionNamespace: string;
              userDataKeys: string[];
            } = JSON.parse(e.data.data);

            const {userDataKeys, progressionNamespace} = data;
            try {
              const userData = await getProgressionUserData(progressionNamespace, userDataKeys);

              if (userData?.payload) {
                // Handle unpopulated fields
                Object.keys(userData.payload).forEach((key) => {
                  const item = userData.payload[key];
                  if (!item['date_updated']) {
                    item['date_updated'] = '1970-01-01T00:00:00.000Z';
                  }
                  if (!item['meta']) {
                    item['meta'] = {};
                  }
                  if (!item['display_name']) {
                    item['display_name'] = '';
                  }
                });

                sendMessageToGame(`${e.data.topic}:callback`, JSON.stringify(userData.payload));
              } else {
                sendMessageToGame(`${e.data.topic}:callback`, undefined, 'No data found');
              }
            } catch (error) {
              sendMessageToGame(`${e.data.topic}:callback`, undefined, error.message);
            }
            break;
          }

          case 'UpdateProgressionUserData': {
            const data: {
              progressionNamespace: string;
              userDataUpdates: Record<string, string>;
            } = JSON.parse(e.data.data);
            const {progressionNamespace, userDataUpdates} = data;

            try {
              const result = await updateProgressionUserData({
                progressionNamespace,
                userDataUpdates,
              });
              if (result === 'success') {
                sendMessageToGame(`${e.data.topic}:callback`, result);
              } else {
                sendMessageToGame(`${e.data.topic}:callback`, undefined, result);
              }
            } catch (error) {
              sendMessageToGame(`${e.data.topic}:callback`, undefined, error.message);
            }

            break;
          }
          case 'GetCurrentSeason': {
            try {
              const currentSeason = await getCurrentSeason();
              const payload = currentSeason.items[0];
              sendMessageToGame('GetCurrentSeason:callback', JSON.stringify(payload));
            } catch (error) {
              sendMessageToGame('GetCurrentSeason:callback', undefined, error.message);
            }
            break;
          }
          case 'GetPreviousSeasons': {
            try {
              const {count} = JSON.parse(e.data.data) as {count: number};
              const previousSeasons = await getPreviousSeasons(count);
              const seasons = previousSeasons.items;
              sendMessageToGame('GetPreviousSeasons:callback', JSON.stringify({seasons}));
            } catch (error) {
              sendMessageToGame('GetPreviousSeasons:callback', undefined, error.message);
            }
            break;
          }
          case 'GetNextSeasons': {
            try {
              const {count} = JSON.parse(e.data.data) as {count: number};
              const nextSeasons = await getNextSeasons(count);
              const seasons = nextSeasons.items;
              sendMessageToGame('GetNextSeasons:callback', JSON.stringify({seasons}));
            } catch (error) {
              sendMessageToGame('GetNextSeasons:callback', undefined, error.message);
            }
            break;
          }
          default:
            console.debug('REPLAY: Received unknown message', JSON.stringify(e.data));
            break;
        }
      } catch (error) {
        console.log(e.data.topic, 'failed');
        console.error(e);
        sendMessageToGame(`${e.data.topic}:callback`, undefined, error.message);
      }
    };

    window.addEventListener('message', readMessageFromGame);
    return () => {
      window.removeEventListener('message', readMessageFromGame);
    };
  }, [
    origin,
    isCash,
    gid,
    endMatch,
    gameInitialized,
    setMatchDetails,
    showProgressionRoom,
    matchDetails,
    showTutorial,
    addStartMatchListener,
    router,
    preload,
  ]);

  const style: CSSProperties | undefined =
    isMatchOngoing || showTutorial || showProgressionRoom
      ? undefined
      : {
          zIndex: -1,
          opacity: 0,
          pointerEvents: 'none',
        };

  return (
    <>
      {showLoadingBar ? <LoadingBar progress={loadingProgress} /> : null}
      {showLoadingScreen ? <LoadingScreen progress={loadingProgress} /> : null}
      {matchDetails || showProgressionRoom ? null : children}
      <iframe
        className='h-[100dvh] w-[100dvw] absolute top-0 left-0 overflow-hidden z-10'
        id={GAME_IFRAME_ID}
        data-testid='game-iframe'
        src={loadLight ? `${src}?load=light` : src}
        allow='autoplay; fullscreen;'
        style={style}
      ></iframe>
    </>
  );
}
